import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Typography, Menu, MenuItem } from '@mui/material'
import TuneIcon from '@mui/icons-material/TuneRounded'
import CheckIcon from '@mui/icons-material/Check'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { colors } from '../../const/colors'
import { IParkingPoolProfile } from '../../graphql/parkingPool/getTenantParkingPoolUsers'
import { SectionActions, SectionButton } from '../Shared/SectionButton'
import { CARD_SX, FIELD_SX, SectionHeader } from './UserDetailsUi'
import { findDefaultProfile } from '../../utils/defaultParkingPoolProfile'
import ProfileLegend, { ProfileLimitGrid } from './ProfileLegend'

const ServiceProfileSection: FC<{
  assigned: boolean
  profileId: string
  profileOptions: IParkingPoolProfile[]
  profileDirty: boolean
  onSelectProfile: (id: string) => void
  onSave: () => void
  step?: number
  stepDone?: boolean
}> = ({
  assigned,
  profileId,
  profileOptions,
  profileDirty,
  onSelectProfile,
  onSave,
  step,
  stepDone,
}) => {
  const { t } = useTranslation(['user_details', 'tenant', 'btn'])
  const [menuEl, setMenuEl] = useState<null | HTMLElement>(null)
  const [legendEl, setLegendEl] = useState<null | HTMLElement>(null)

  const selectable = profileOptions.length >= 2
  const defaultProfile = findDefaultProfile(profileOptions)
  const defaultProfileId = defaultProfile?.id
  const currentProfile =
    profileOptions.find(p => p.id === profileId) ??
    defaultProfile ??
    profileOptions[0]
  const currentProfileName =
    currentProfile?.name ?? t('parking_pool:no_profile')

  const showDefaultHint =
    !assigned && profileOptions.length > 0 && (!selectable || !!defaultProfile)
  const defaultHint = selectable
    ? t('default_profile_hint_multiple')
    : t('default_profile_hint_single')

  return (
    <Box sx={CARD_SX}>
      <SectionHeader
        icon={<TuneIcon sx={{ fontSize: 18 }} />}
        title={t('service_profile')}
        step={step}
        done={stepDone}
        action={
          !!profileOptions.length && (
            <Box
              onClick={e => setLegendEl(e.currentTarget)}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                p: '5px 10px',
                borderRadius: colors.radiusTag,
                color: legendEl ? colors.navy : colors.textMuted,
                background: legendEl ? colors.rowSelected : 'transparent',
                fontSize: '11.5px',
                fontWeight: 700,
                cursor: 'pointer',
                flexShrink: 0,
                whiteSpace: 'nowrap',
                transition: 'color .15s, background .15s',
                '&:hover': { color: colors.navy, background: colors.bg },
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 15 }} />
              {t('profile_legend_open')}
            </Box>
          )
        }
      />
      <Box
        onClick={e => selectable && setMenuEl(e.currentTarget)}
        data-e2e="service-profile-select"
        sx={{
          ...FIELD_SX,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: selectable ? 'pointer' : 'default',
          transition: 'border-color .15s',
          '&:hover': selectable ? { borderColor: colors.navy } : undefined,
        }}
      >
        {currentProfileName}
        {selectable && (
          <KeyboardArrowDownIcon
            sx={{ fontSize: 20, color: colors.slateLight }}
          />
        )}
      </Box>
      <ProfileLimitGrid profile={currentProfile} framed />
      {showDefaultHint && (
        <Box
          data-e2e="default-profile-hint"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            mt: '9px',
            fontSize: '12px',
            color: colors.textMuted,
            fontWeight: 600,
            lineHeight: 1.4,
          }}
        >
          <InfoOutlinedIcon
            sx={{ fontSize: 15, color: colors.slate, flexShrink: 0 }}
          />
          {defaultHint}
        </Box>
      )}
      {assigned && (
        <SectionActions>
          <SectionButton
            label={t('btn:save')}
            icon={<CheckIcon sx={{ fontSize: 17 }} />}
            disabled={!profileDirty}
            onClick={onSave}
          />
        </SectionActions>
      )}
      <Menu
        anchorEl={menuEl}
        open={!!menuEl}
        onClose={() => setMenuEl(null)}
        slotProps={{
          paper: { sx: { minWidth: menuEl?.offsetWidth, maxWidth: 420 } },
        }}
      >
        {profileOptions.map(option => (
          <MenuItem
            key={option.id}
            selected={option.id === profileId}
            onClick={() => {
              onSelectProfile(option.id)
              setMenuEl(null)
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              py: '10px',
            }}
          >
            <Typography sx={{ fontSize: '14px', fontWeight: 700 }}>
              {option.name}
            </Typography>
            {option.id === defaultProfileId && (
              <Box
                component="span"
                sx={{
                  background: colors.rowSelected,
                  color: colors.navy,
                  fontSize: '10.5px',
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  p: '2px 7px',
                  borderRadius: colors.radiusTag,
                }}
              >
                {t('profile_badge_default')}
              </Box>
            )}
          </MenuItem>
        ))}
      </Menu>
      <ProfileLegend
        anchorEl={legendEl}
        onClose={() => setLegendEl(null)}
        profiles={profileOptions}
        defaultProfileId={defaultProfileId}
        assignedProfileId={assigned ? profileId : undefined}
      />
    </Box>
  )
}

export default ServiceProfileSection
